<script lang="ts" setup>
import './scss/wizard.scss';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { loadLocaleMessages } from '../../../helpers/lang/loadLocale';
import i18n from '../../../helpers/lang/i18n';
import LanguagesRepository from '@repositories/languages.repository';
import StepCotiza from './steps/01-StepCotiza.vue';
import StepDatosEnvio from './steps/02-StepDatosEnvio.vue';
import StepRevisaDatos from './steps/03-StepRevisaDatos.vue';
import StepDetalleViaje from './steps/04-StepDetalleViaje.vue';
import StepTuPoliza from './steps/05-StepTuPoliza.vue';
import { scrollAncestorsToTop } from '../shared/scrollUtils';
import storePolicy from '@store/policy';

const { t } = useI18n();
const store = storePolicy();

const wizardRoot = ref<HTMLElement | null>(null);
const step = ref<number>(1);
// updatesClientInputs producido por el paso "Datos de envío" (RFC) para precargar "Revisa tus datos"
const clientUpdates = ref<any[]>([]);
const policyId = ref<number | null>(null);

const WHATSAPP_URL =
  'https://wa.me/525546194648?text=Hola,%20quiero%20cotizar%20un%20seguro';

const stepsNav = computed(() => [
  { n: 1, label: t('landing.wizard.stepper.cotiza') },
  { n: 2, label: t('landing.wizard.stepper.tus_datos') },
  { n: 3, label: t('landing.wizard.stepper.revisa_datos') },
  { n: 4, label: t('landing.wizard.stepper.detalle_viaje') },
  { n: 5, label: t('landing.wizard.stepper.tu_poliza') },
]);

onMounted(async () => {
  const lang = await LanguagesRepository.getLanguageByUrlDomain(
    window.location.origin,
  );
  const messages = loadLocaleMessages(lang);
  i18n.global.setLocaleMessage(lang, messages);
  i18n.global.locale.value = lang;
});

const goTo = (n: number) => {
  step.value = n;
  scrollAncestorsToTop(wizardRoot.value);
};

const handleCotiza = () => goTo(2);
const handleDatosEnvio = (payload: { updatesClientInputs?: any[] }) => {
  clientUpdates.value = payload?.updatesClientInputs ?? [];
  goTo(3);
};
const handleRevisa = () => goTo(4);
const handleDetalle = (id: number) => {
  policyId.value = id;
  goTo(5);
};

// "Cotizar otro viaje": limpia el estado y vuelve al paso 1 sin depender
// de la navegación del router (ver nota en StepTuPoliza.vue).
const handleRestart = () => {
  store.$reset();
  clientUpdates.value = [];
  policyId.value = null;
  goTo(1);
};
</script>

<template>
  <div class="tryger-wizard" ref="wizardRoot">
    <header class="wz-header">
      <div class="wz-header-inner">
        <a href="/" class="wz-logo" aria-label="Tryger">
          <img src="/img/tryger-logo.png" alt="Tryger" />
        </a>
        <div class="wz-header-trust">
          <div class="chubb-badge">
            <span>{{ t('landing.components.footer.asegurado_por') }}</span>
            <img src="/img/logo-chubb.png" alt="Chubb" class="chubb-badge__logo" />
          </div>
        </div>
        <div class="wz-header-help">
          {{ t('landing.wizard.header.dudas') }}
          <a :href="WHATSAPP_URL" target="_blank">
            {{ t('landing.wizard.header.habla_con_asesor') }}
          </a>
        </div>
      </div>
    </header>

    <nav class="wz-steps" aria-label="Progreso">
      <div class="wz-steps-inner">
        <template v-for="(s, i) in stepsNav" :key="s.n">
          <div
            class="wz-step"
            :class="{ active: step === s.n, done: step > s.n }"
          >
            <span class="wz-step-num">{{ s.n }}</span>
            <span>{{ s.label }}</span>
          </div>
          <span v-if="i < stepsNav.length - 1" class="wz-step-arrow">›</span>
        </template>
      </div>
    </nav>

    <StepCotiza v-if="step === 1" @next="handleCotiza" />
    <StepDatosEnvio v-else-if="step === 2" @next="handleDatosEnvio" @back="goTo(1)" />
    <StepRevisaDatos
      v-else-if="step === 3"
      :client-updates="clientUpdates"
      @next="handleRevisa"
      @back="goTo(2)"
    />
    <StepDetalleViaje
      v-else-if="step === 4"
      @next="handleDetalle"
      @back="goTo(3)"
    />
    <StepTuPoliza v-else :policy-id="policyId" @restart="handleRestart" />

    <footer class="wz-footer">
      <p class="wz-footer-tagline">{{ t('landing.wizard.footer.tagline') }}</p>
      <div class="wz-footer-powered">
        {{ t('landing.wizard.footer.powered') }}
      </div>
    </footer>

    <a :href="WHATSAPP_URL" class="wz-wa-float" target="_blank" rel="noopener">
      <img src="/icons/whatsapp.svg" alt="" class="wz-wa-icon" />
      <span class="wz-wa-text">{{ t('landing.wizard.header.habla_con_asesor') }}</span>
    </a>
  </div>
</template>
